import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  Radio,
  Slider,
  Typography,
} from "@material-ui/core";

import { lprScoringServiceInstance as lprScoringService } from "../../../services/LPRScoringService";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import LPRScoringCard from "../../LPRScoring/LPRScoringCard";
import useScoreKeyboardShortcuts from "../../LPRScoring/useScoreKeyboardShortcuts";
import { useStyles } from "./index.styles";

const MIN_SCORE = 0;
const MAX_SCORE = 10;

const deriveInitialScore = (read) => {
  if (!read) return { incorrectCharacters: MIN_SCORE, isException: false };
  if (read.isException === true) {
    return { incorrectCharacters: MIN_SCORE, isException: true };
  }
  return {
    incorrectCharacters: read.incorrectCharacters ?? MIN_SCORE,
    isException: false,
  };
};

const isUnscored = (read) =>
  Boolean(read) &&
  (read.incorrectCharacters === null || read.incorrectCharacters === undefined) &&
  (read.isException === null || read.isException === undefined);

const LPRScoreEditDrawer = ({ read, open, onClose, onSaved }) => {
  const classes = useStyles();
  const enqueueSnackbar = useEnqueueSnackbar();

  const initialScore = useMemo(() => deriveInitialScore(read), [read]);

  const [incorrectCharacters, setIncorrectCharacters] = useState(initialScore.incorrectCharacters);
  const [isException, setIsException] = useState(initialScore.isException);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const next = deriveInitialScore(read);
    setIncorrectCharacters(next.incorrectCharacters);
    setIsException(next.isException);
    setIsDirty(false);
    setIsSaving(false);
  }, [read]);

  const canSave = (isUnscored(read) || isDirty) && !isSaving;

  const cardRead = useMemo(() => {
    if (!read) return null;
    return {
      lprId: read.lprId,
      plateText: read.plateText,
      confidence: read.confidence,
      isCaptureImageAvailable: read.isImageAvailable,
      isContextImageAvailable: true,
    };
  }, [read]);

  const handleSliderChange = (_, value) => {
    setIncorrectCharacters(value);
    if (isException) {
      setIsException(false);
    }
    setIsDirty(true);
  };

  const handleExceptionClick = () => {
    setIsException((prev) => {
      const next = !prev;
      if (next) {
        setIncorrectCharacters(MIN_SCORE);
      }
      return next;
    });
    setIsDirty(true);
  };

  const setScoreFromKeyboard = useCallback((value) => {
    setIncorrectCharacters(value);
    setIsException(false);
    setIsDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!read) return;
    const payload = {
      incorrectCharacters: isException ? null : incorrectCharacters,
      isException,
    };
    setIsSaving(true);
    try {
      await lprScoringService.saveScore(read.lprId, payload);
      if (onSaved) onSaved(read.lprId, payload);
      onClose();
    } catch (err) {
      enqueueSnackbar("Failed to save the score", {
        variant: "error",
        tag: "LPRScoreSaveFailed",
      });
    } finally {
      setIsSaving(false);
    }
  }, [read, isException, incorrectCharacters, onSaved, onClose, enqueueSnackbar]);

  useScoreKeyboardShortcuts({
    enabled: open && Boolean(read),
    canSave,
    onSetScore: setScoreFromKeyboard,
    onSave: handleSave,
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.drawerPaper }}
    >
      <div className={classes.drawerInner}>
        <div className={classes.drawerHeader}>
          <Typography variant="h6" color="primary">
            {read?.plateText || "Score read"}
          </Typography>
        </div>

        <Divider />

        <div className={classes.drawerBody}>
          <LPRScoringCard read={cardRead} />

          <Typography variant="body2" className={classes.sliderLabel}>
            Missing or incorrect characters
          </Typography>
          <Box className={classes.sliderRow}>
            <Slider
              value={incorrectCharacters}
              onChange={handleSliderChange}
              min={MIN_SCORE}
              max={MAX_SCORE}
              step={1}
              valueLabelDisplay="auto"
              disabled={isSaving}
              aria-label="Missing or incorrect characters"
              data-testid="lpr-score-edit-slider"
            />
            <Typography className={classes.sliderValue} data-testid="lpr-score-edit-slider-value">
              {incorrectCharacters}
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Radio
                checked={isException}
                onClick={handleExceptionClick}
                color="primary"
                disabled={isSaving}
                data-testid="lpr-score-edit-exception"
              />
            }
            label="Exception"
          />
        </div>

        <Divider />

        <div className={classes.drawerFooter}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSaving}
            data-testid="lpr-score-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!canSave}
            startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
            data-testid="lpr-score-edit-save"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default LPRScoreEditDrawer;
